// Pricing migration 32 (ADO-3985), T14: what the side cart shows in place of its lines when the
// shop opens a deal from it (Option A, D-7).
//   CartPanelDeal          the deal in the panel (15: "Mobile layout of the deal in the panel,
//                          "← Current order" back. Never a dialog on top of the panel."), live
//                          from the deals store.
//   CartPanelDealsToFinish the list of deals to finish (R8 16b: "2 deals to finish" / "Pick one
//                          to open it here. You can come back to this list from the deal."), for
//                          "Show me" with two or more incomplete deals.
// A deal opened from the list goes back to the list with "← 2 deals", the list's count (16b's
// note: ""← 2 deals" returns here").

import { useRef } from 'react';
import type { MultibuyDeal } from '../api/multibuy-deals.client';
import { Icon } from '../design-system';
import type { CartDealToFinish } from './cartDeals';
import { MultibuyDealView } from './MultibuyDealView';
import { useMultibuyDealsEntry } from './useMultibuyDealsStore';
import styles from './CartPanelDealViews.module.css';

function BackLink({ label, onBack }: { readonly label: string; readonly onBack: () => void }) {
  return (
    <div className={styles.backRow}>
      <button type="button" className={styles.back} data-panel-back="" onClick={onBack}>
        <Icon name="arrowLeft" size={16} />
        {label}
      </button>
    </div>
  );
}

export function CartPanelDeal({
  skuCode,
  offerCode,
  backLabel,
  onBack,
  onDone,
  touch,
}: {
  readonly skuCode: string;
  readonly offerCode: string;
  readonly backLabel: string;
  readonly onBack: () => void;
  readonly onDone: () => void;
  readonly touch: boolean;
}) {
  const entry = useMultibuyDealsEntry(skuCode);
  const lastShown = useRef<MultibuyDeal | null>(null);
  const live =
    entry?.state === 'ready'
      ? (entry.deals.find((deal) => deal.multibuyOfferCode === offerCode) ?? null)
      : null;
  if (live !== null) lastShown.current = live;
  const deal = lastShown.current;
  return (
    <div className={styles.view} data-panel-view="deal">
      <BackLink label={backLabel} onBack={onBack} />
      {deal === null ? null : (
        <MultibuyDealView deal={deal} layout="panel" touch={touch} onDone={onDone} />
      )}
    </div>
  );
}

export function CartPanelDealsToFinish({
  rows,
  onOpen,
  onBack,
}: {
  readonly rows: readonly CartDealToFinish[];
  readonly onOpen: (deal: MultibuyDeal) => void;
  readonly onBack: () => void;
}) {
  return (
    <div className={styles.view} data-panel-view="deals-to-finish">
      <BackLink label="Current order" onBack={onBack} />
      <div className={styles.list}>
        <h3 className={styles.heading}>
          {rows.length === 1 ? '1 deal to finish' : `${rows.length} deals to finish`}
        </h3>
        <p className={styles.body}>
          Pick one to open it here. You can come back to this list from the deal.
        </p>
        <ul className={styles.rows}>
          {rows.map((row) => (
            <li key={row.deal.multibuyOfferCode}>
              <button type="button" className={styles.row} onClick={() => onOpen(row.deal)}>
                <Icon name="gift" size={16} />
                <span className={styles.rowText}>{row.text}</span>
                <Icon name="arrowRight" size={16} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
